import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import teams from "./teams";

const TOURNAMENT_START = new Date("2026-06-11T19:00:00Z");
const tournamentStarted = () => new Date() >= TOURNAMENT_START;

const POINTS = {
  goalGroup: 1, goalKnockout: 2, groupWin: 3, groupDraw: 1,
  advanceToKnockout: 2, groupWinner: 5, r32Win: 10, r16Win: 20,
  qfWin: 40, sfWin: 80, champion: 150,
};

function Flag({ cc, size = 16 }) {
  if (!cc) return null;
  const h = Math.round(size * 0.75);
  return (
    <img src={"https://flagcdn.com/" + size + "x" + h + "/" + cc.toLowerCase() + ".png"}
      alt="" width={size} height={h}
      style={{ objectFit: "cover", borderRadius: 2, flexShrink: 0, verticalAlign: "middle" }}
      onError={(e) => { e.target.style.display = "none"; }} />
  );
}

function getRankStyle(index) {
  if (index === 0) return { medal: "🥇", border: "rgba(255,215,0,0.6)",    bg: "#2a2e0a" };
  if (index === 1) return { medal: "🥈", border: "rgba(192,192,192,0.7)",  bg: "#2a2e35" };
  if (index === 2) return { medal: "🥉", border: "rgba(205,127,50,0.6)",   bg: "#2a2010" };
  return { medal: null, border: "rgba(255,255,255,0.07)", bg: "#1a2d4a" };
}

function computeBreakdown(picks, teamStatsMap) {
  let gg=0, kg=0, gw=0, gd=0, adv=0, gwb=0, r32=0, r16=0, qf=0, sf=0, champ=0;
  for (let tier = 1; tier <= 10; tier++) {
    const localId = picks["tier" + tier];
    if (!localId) continue;
    const team = teams.find((t) => t.id === localId);
    if (!team) continue;
    const s = teamStatsMap[String(team.apiId)];
    if (!s) continue;
    gg    += (s.goals || 0) * POINTS.goalGroup;
    kg    += (s.knockoutGoals || 0) * POINTS.goalKnockout;
    gw    += (s.groupWins || 0) * POINTS.groupWin;
    gd    += (s.groupDraws || 0) * POINTS.groupDraw;
    adv   += s.advancedToKnockout ? POINTS.advanceToKnockout : 0;
    gwb   += s.groupWinner ? POINTS.groupWinner : 0;
    r32   += s.r32Win ? POINTS.r32Win : 0;
    r16   += s.r16Win ? POINTS.r16Win : 0;
    qf    += s.qfWin ? POINTS.qfWin : 0;
    sf    += s.sfWin ? POINTS.sfWin : 0;
    champ += s.champion ? POINTS.champion : 0;
  }
  const groupStage    = gg + gw + gd + adv + gwb;
  const knockoutStage = kg + r32 + r16 + qf + sf + champ;
  const goals         = gg + kg;
  return { gg, kg, gw, gd, adv, gwb, r32, r16, qf, sf, champ, groupStage, knockoutStage, goals };
}

function teamBreakdown(team, stats) {
  if (!stats) return null;
  const gg    = (stats.goals || 0) * POINTS.goalGroup;
  const kg    = (stats.knockoutGoals || 0) * POINTS.goalKnockout;
  const gw    = (stats.groupWins || 0) * POINTS.groupWin;
  const gd    = (stats.groupDraws || 0) * POINTS.groupDraw;
  const adv   = stats.advancedToKnockout ? POINTS.advanceToKnockout : 0;
  const gwb   = stats.groupWinner ? POINTS.groupWinner : 0;
  const r32   = stats.r32Win ? POINTS.r32Win : 0;
  const r16   = stats.r16Win ? POINTS.r16Win : 0;
  const qf    = stats.qfWin ? POINTS.qfWin : 0;
  const sf    = stats.sfWin ? POINTS.sfWin : 0;
  const champ = stats.champion ? POINTS.champion : 0;
  const total = gg + kg + gw + gd + adv + gwb + r32 + r16 + qf + sf + champ;
  return { gg, kg, gw, gd, adv, gwb, r32, r16, qf, sf, champ, total };
}

function EntryBreakdown({ picks, teamStatsMap, teamGamesPlayed = {} }) {
  const rows = [];
  for (let tier = 1; tier <= 10; tier++) {
    const localId = picks["tier" + tier];
    if (!localId) continue;
    const team = teams.find((t) => t.id === localId);
    if (!team) continue;
    const stats = teamStatsMap[String(team.apiId)];
    const bd = teamBreakdown(team, stats);
    const gp = teamGamesPlayed[String(team.apiId)] || 0;
    rows.push({ tier, team, bd, gp });
  }
  const totalGP = rows.reduce((a, r) => a + (r.gp || 0), 0);

  const totalRow = rows.reduce((acc, r) => {
    if (!r.bd) return acc;
    return {
      gg: acc.gg + r.bd.gg, kg: acc.kg + r.bd.kg,
      gw: acc.gw + r.bd.gw, gd: acc.gd + r.bd.gd,
      adv: acc.adv + r.bd.adv, gwb: acc.gwb + r.bd.gwb,
      r32: acc.r32 + r.bd.r32, r16: acc.r16 + r.bd.r16,
      qf: acc.qf + r.bd.qf, sf: acc.sf + r.bd.sf,
      champ: acc.champ + r.bd.champ, total: acc.total + r.bd.total,
    };
  }, { gg:0,kg:0,gd:0,gw:0,adv:0,gwb:0,r32:0,r16:0,qf:0,sf:0,champ:0,total:0 });

  // Knockout columns only shown once any team in this entry has earned knockout points
  const koKeys = ["kg", "adv", "gwb", "r32", "r16", "qf", "sf", "champ"];
  const anyKnockout = koKeys.some((k) => rows.some((r) => r.bd && r.bd[k] > 0));

  const baseCols = [
    { key: "total", label: "PTS", title: "Total points for this team" },
    { key: "gg",    label: "GG",  title: "Group goals (×1pt each)" },
    { key: "gw",    label: "GW",  title: "Group wins (×3pts each)" },
    { key: "gd",    label: "GD",  title: "Group draws (×1pt each)" },
  ];
  const koCols = [
    { key: "kg",    label: "KG",  title: "Knockout goals (×2pts each)" },
    { key: "adv",   label: "ADV", title: "Advanced to knockout (+2pts)" },
    { key: "gwb",   label: "GW+", title: "Group winner bonus (+5pts)" },
    { key: "r32",   label: "R32", title: "Round of 32 win (+10pts)" },
    { key: "r16",   label: "R16", title: "Round of 16 win (+20pts)" },
    { key: "qf",    label: "QF",  title: "Quarterfinal win (+40pts)" },
    { key: "sf",    label: "SF",  title: "Semifinal win (+80pts)" },
    { key: "champ", label: "🏆",  title: "Champion (+150pts)" },
  ];
  const cols = anyKnockout ? [...baseCols, ...koCols] : baseCols;

  const thStyle = {
    padding: "5px 6px", fontSize: 10, fontWeight: 700,
    color: "rgba(255,255,255,0.4)", letterSpacing: 0.5,
    textAlign: "center", whiteSpace: "nowrap", borderBottom: "1px solid rgba(255,255,255,0.08)"
  };
  const tdBase = { padding: "6px 6px", textAlign: "center", fontSize: 12 };

  return (
    <div style={{ overflowX: "auto", marginTop: 4 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: anyKnockout ? 620 : 360 }}>
        <thead>
          <tr style={{ background: "#101e30" }}>
            <th style={{ ...thStyle, textAlign: "left", paddingLeft: 10, minWidth: 130 }}>TEAM</th>
            <th style={thStyle} title="Group games played (of 3)">GP</th>
            {cols.map((c) => (
              <th key={c.key} style={thStyle} title={c.title}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ tier, team, bd, gp }) => (
            <tr key={tier} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <td style={{ ...tdBase, textAlign: "left", paddingLeft: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 9, color: "rgba(255,215,0,0.5)", fontWeight: 700, minWidth: 16 }}>T{tier}</span>
                  <Flag cc={team.cc} size={14} />
                  <span style={{ fontWeight: 600, fontSize: 12, color: "white" }}>{team.name}</span>
                </div>
              </td>
              <td style={{ ...tdBase, color: gp > 0 ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.25)", fontWeight: 600 }}>
                {gp}/3
              </td>
              {bd ? cols.map((c) => {
                const val = bd[c.key];
                const isTotal = c.key === "total";
                const isNonZero = val > 0;
                return (
                  <td key={c.key} style={{
                    ...tdBase,
                    color: isTotal ? "#FFD700" : isNonZero ? "#00C853" : "rgba(255,255,255,0.2)",
                    fontWeight: isTotal || isNonZero ? 700 : 400,
                    fontFamily: isTotal ? "'Bebas Neue', sans-serif" : "inherit",
                    fontSize: isTotal ? 15 : 12,
                    background: isTotal ? "rgba(255,215,0,0.04)" : "transparent",
                  }}>
                    {val > 0 ? val : "—"}
                  </td>
                );
              }) : cols.map((c) => (
                <td key={c.key} style={{ ...tdBase, color: "rgba(255,255,255,0.15)" }}>—</td>
              ))}
            </tr>
          ))}
          <tr style={{ borderTop: "2px solid rgba(255,255,255,0.1)", background: "rgba(255,215,0,0.04)" }}>
            <td style={{ ...tdBase, textAlign: "left", paddingLeft: 10, fontWeight: 700, fontSize: 11, color: "rgba(255,255,255,0.5)", letterSpacing: 1 }}>
              TOTAL
            </td>
            <td style={{ ...tdBase, fontWeight: 700, fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
              {totalGP}/30
            </td>
            {cols.map((c) => {
              const val = totalRow[c.key];
              const isTotal = c.key === "total";
              return (
                <td key={c.key} style={{
                  ...tdBase,
                  color: isTotal ? "#FFD700" : val > 0 ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.2)",
                  fontWeight: 700,
                  fontFamily: isTotal ? "'Bebas Neue', sans-serif" : "inherit",
                  fontSize: isTotal ? 17 : 12,
                }}>
                  {val > 0 ? val : "—"}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 8, padding: "0 4px" }}>
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
          <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>GP</span> = group games played (of 3)
        </span>
        {cols.filter((c) => c.key !== "total").map((c) => (
          <span key={c.key} style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
            <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>{c.label}</span> = {c.title}
          </span>
        ))}
      </div>
    </div>
  );
}

const SORT_OPTIONS = [
  { key: "total",         label: "Total Score" },
  { key: "groupStage",    label: "Group Stage" },
  { key: "knockoutStage", label: "Knockout" },
  { key: "goals",         label: "All Goals" },
  { key: "gg",            label: "Group Goals" },
  { key: "kg",            label: "Knockout Goals" },
];

function Leaderboard({ contest, user, onEditEntry }) {
  const [entries, setEntries]         = useState([]);
  const [teamStatsMap, setTeamStatsMap] = useState({});
  const [fixtures, setFixtures]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expandedEntries, setExpandedEntries] = useState({});
  const [sortKey, setSortKey]         = useState("total");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const [entriesSnap, statsSnap, fixturesSnap] = await Promise.all([
      getDocs(query(collection(db, "entries"), where("contestId", "==", contest.id))),
      getDocs(collection(db, "teamStats")),
      getDocs(collection(db, "fixtures"))
    ]);
    const data = entriesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const statsMap = {};
    statsSnap.docs.forEach((d) => { statsMap[d.id] = d.data(); });
    setEntries(data);
    setTeamStatsMap(statsMap);
    setFixtures(fixturesSnap.docs.map((d) => d.data()));
    setLoading(false);
  };

  const started = tournamentStarted();

  // Group-stage progress: count group matches played vs total
  const groupProgress = useMemo(() => {
    const groupFixtures = fixtures.filter((f) => f.round && f.round.startsWith("Group"));
    const played = groupFixtures.filter((f) => f.status === "FT").length;
    const live = groupFixtures.filter((f) => f.status === "Live").length;
    const total = groupFixtures.length || 72; // 2026 WC has 72 group matches
    // Matchday parity points: every team has played the same number of games at 1/3, 2/3, 3/3
    const md = Math.round(total / 3); // 24 games = one full matchday for 48 teams
    const markers = [md, md * 2, md * 3].filter((m) => m <= total);
    // Which matchday are we currently completing? (0,1,2,3)
    const matchdaysComplete = markers.filter((m) => played >= m).length;
    const atParity = markers.includes(played); // exactly on a parity line
    return { played, live, total, markers, matchdaysComplete, atParity };
  }, [fixtures]);

  // Per-team: how many group games each team (by apiId) has completed
  const teamGamesPlayed = useMemo(() => {
    const map = {};
    fixtures.forEach((f) => {
      if (!f.round || !f.round.startsWith("Group")) return;
      if (f.status !== "FT") return;
      if (f.homeTeamId != null) map[String(f.homeTeamId)] = (map[String(f.homeTeamId)] || 0) + 1;
      if (f.awayTeamId != null) map[String(f.awayTeamId)] = (map[String(f.awayTeamId)] || 0) + 1;
    });
    return map;
  }, [fixtures]);

  // Determine which teams are still alive in the knockout stage.
  // A team is eliminated if it appears as the loser in a completed knockout fixture.
  const knockoutInfo = useMemo(() => {
    const koFixtures = fixtures.filter((f) => f.round && !f.round.startsWith("Group"));
    const knockoutStarted = koFixtures.length > 0;
    const eliminated = new Set();
    const inKnockout = new Set();
    koFixtures.forEach((f) => {
      if (f.homeTeamId != null) inKnockout.add(String(f.homeTeamId));
      if (f.awayTeamId != null) inKnockout.add(String(f.awayTeamId));
      if (f.status === "FT") {
        // loser is eliminated; determine by score (knockouts have a winner)
        const hs = f.homeScore, as = f.awayScore;
        if (hs != null && as != null) {
          if (hs > as && f.awayTeamId != null) eliminated.add(String(f.awayTeamId));
          else if (as > hs && f.homeTeamId != null) eliminated.add(String(f.homeTeamId));
          // ties go to penalties; if winner flag exists use it
          else if (hs === as) {
            if (f.homeWinner === true && f.awayTeamId != null) eliminated.add(String(f.awayTeamId));
            else if (f.awayWinner === true && f.homeTeamId != null) eliminated.add(String(f.homeTeamId));
          }
        }
      }
    });
    // Remaining = in knockout bracket AND not eliminated
    const remaining = [...inKnockout].filter((id) => !eliminated.has(id));

    // Determine the current knockout round: the earliest round that still has
    // games not yet finished; if all are FT, use the latest round present.
    const ROUND_ORDER = ["Round of 32", "Round of 16", "Quarter-finals", "Semi-finals", "Final"];
    const roundsPresent = ROUND_ORDER.filter((r) => koFixtures.some((f) => f.round === r));
    let currentRound = "";
    for (const r of ROUND_ORDER) {
      const rf = koFixtures.filter((f) => f.round === r);
      if (rf.length > 0 && rf.some((f) => f.status !== "FT")) { currentRound = r; break; }
    }
    if (!currentRound && roundsPresent.length > 0) currentRound = roundsPresent[roundsPresent.length - 1];

    return { knockoutStarted, remaining: new Set(remaining), eliminated, currentRound };
  }, [fixtures]);

  // Build sorted list of remaining team objects for the header display
  const remainingTeams = useMemo(() => {
    return teams
      .filter((t) => t.apiId != null && knockoutInfo.remaining.has(String(t.apiId)))
      .sort((a, b) => a.tier - b.tier);
  }, [knockoutInfo]);

  // For one entry: which of their picked teams are still alive
  const entryRemainingTeams = (picks) => {
    const out = [];
    for (let tier = 1; tier <= 10; tier++) {
      const localId = (picks || {})["tier" + tier];
      if (!localId) continue;
      const team = teams.find((t) => t.id === localId);
      if (team && team.apiId != null && knockoutInfo.remaining.has(String(team.apiId))) out.push(team);
    }
    return out;
  };

  // For one entry's picks, total group games played across their 10 teams (max 30)
  const entryGamesPlayed = (picks) => {
    let played = 0;
    for (let tier = 1; tier <= 10; tier++) {
      const localId = (picks || {})["tier" + tier];
      if (!localId) continue;
      const team = teams.find((t) => t.id === localId);
      if (team && team.apiId != null) played += teamGamesPlayed[String(team.apiId)] || 0;
    }
    return played;
  };

  const enrichedEntries = useMemo(() => {
    return entries.map((e) => ({
      ...e,
      _bd: computeBreakdown(e.picks || {}, teamStatsMap),
    }));
  }, [entries, teamStatsMap]);

  // Total-score rank is fixed regardless of current sort key
  const totalRankMap = useMemo(() => {
    const ranked = [...enrichedEntries].sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0));
    const map = {};
    ranked.forEach((e, i) => { map[e.id] = i; });
    return map;
  }, [enrichedEntries]);

  const sortedEntries = useMemo(() => {
    return [...enrichedEntries].sort((a, b) => {
      const aVal = sortKey === "total" ? (a.totalScore || 0) : (a._bd[sortKey] || 0);
      const bVal = sortKey === "total" ? (b.totalScore || 0) : (b._bd[sortKey] || 0);
      return bVal - aVal;
    });
  }, [enrichedEntries, sortKey]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#FFD700", letterSpacing: 1 }}>LEADERBOARD</div>
        <button onClick={fetchData} className="btn btn-ghost btn-sm">↻ Refresh</button>
      </div>

      {/* Group stage progress banner — only during group stage */}
      {started && groupProgress.total > 0 && !knockoutInfo.knockoutStarted && (
        <div style={{ marginBottom: 16, background: "#1a2d4a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: 1 }}>
              GROUP STAGE PROGRESS
            </span>
            <span style={{ fontSize: 12, color: "#FFD700", fontWeight: 700 }}>
              {groupProgress.played} / {groupProgress.total} games
              {groupProgress.live > 0 && <span style={{ color: "#E53935", marginLeft: 6 }}>· {groupProgress.live} live</span>}
            </span>
          </div>
          <div style={{ position: "relative", height: 10, borderRadius: 5, background: "rgba(255,255,255,0.08)", overflow: "visible" }}>
            <div style={{
              height: "100%",
              width: `${Math.round((groupProgress.played / groupProgress.total) * 100)}%`,
              background: "linear-gradient(90deg, #FFD700, #C9A227)",
              borderRadius: 5,
              transition: "width 0.4s ease"
            }} />
            {/* Matchday parity markers */}
            {groupProgress.markers.map((m, i) => {
              const pct = (m / groupProgress.total) * 100;
              const reached = groupProgress.played >= m;
              return (
                <div key={m} style={{ position: "absolute", left: `${pct}%`, top: -3, bottom: -3, transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{
                    width: 2, height: 16,
                    background: reached ? "#fff" : "rgba(255,255,255,0.3)",
                    boxShadow: reached ? "0 0 6px rgba(255,255,255,0.8)" : "none"
                  }} />
                </div>
              );
            })}
          </div>
          {/* Matchday labels under the markers */}
          <div style={{ position: "relative", height: 12, marginTop: 2 }}>
            {groupProgress.markers.map((m, i) => {
              const pct = (m / groupProgress.total) * 100;
              const reached = groupProgress.played >= m;
              const isLast = i === groupProgress.markers.length - 1;
              return (
                <span key={m} style={{ position: "absolute", left: `${pct}%`, transform: isLast ? "translateX(-100%)" : "translateX(-50%)", fontSize: 8, fontWeight: 700, letterSpacing: 0.5, color: reached ? "#FFD700" : "rgba(255,255,255,0.3)", whiteSpace: "nowrap" }}>
                  MD{i + 1}
                </span>
              );
            })}
          </div>
          {groupProgress.atParity ? (
            <div style={{ fontSize: 11, color: "#00C853", marginTop: 6, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}>
              ✓ Matchday {groupProgress.matchdaysComplete} complete — every team has played {groupProgress.matchdaysComplete}, scores are apples-to-apples
            </div>
          ) : (
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 6 }}>
              {groupProgress.total - groupProgress.played} games remaining · next scoring parity at {groupProgress.markers.find((m) => m > groupProgress.played) || groupProgress.total} games
            </div>
          )}
        </div>
      )}

      {/* Knockout: teams remaining banner */}
      {started && knockoutInfo.knockoutStarted && (
        <div style={{ marginBottom: 16, background: "#1a2d4a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: 1 }}>
              {knockoutInfo.currentRound ? knockoutInfo.currentRound.toUpperCase() + " — TEAMS REMAINING" : "TEAMS REMAINING"}
            </span>
            <span style={{ fontSize: 16, color: "#FFD700", fontWeight: 800 }}>
              {remainingTeams.length}
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {remainingTeams.map((t) => (
              <div key={t.id} title={t.name} style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.05)", borderRadius: 5, padding: "5px 6px" }}>
                <Flag cc={t.cc} size={16} />
              </div>
            ))}
            {remainingTeams.length === 0 && (
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>Awaiting knockout results…</span>
            )}
          </div>
        </div>
      )}

      {/* Sort filter */}
      <div style={{ marginBottom: 16, background: "#1a2d4a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "10px 14px" }}>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginBottom: 8, fontWeight: 700, letterSpacing: 1 }}>SORT BY</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {SORT_OPTIONS.map((opt) => (
            <button key={opt.key} onClick={() => setSortKey(opt.key)}
              className={"btn btn-sm " + (sortKey === opt.key ? "btn-primary" : "btn-ghost")}
              style={{ fontSize: 12 }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {!started && (
        <div style={{ background: "#1a2d4a", border: "1px solid rgba(255,215,0,0.25)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "rgba(255,215,0,0.8)", display: "flex", alignItems: "center", gap: 8 }}>
          🔒 Picks are hidden until the tournament kicks off on June 11, 2026.
        </div>
      )}

      {loading && <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading...</p>}
      {!loading && entries.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.4)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <p>No entries yet. Be the first to submit!</p>
        </div>
      )}

      {sortedEntries.map((entry, index) => {
        const isOwn     = entry.userId === user.uid;
        const isExpanded = !!expandedEntries[entry.id];
        const totalRank = totalRankMap[entry.id] ?? index;
        const rank      = getRankStyle(totalRank);
        const canSeePicks = started || isOwn;
        const sortScore = sortKey === "total" ? (entry.totalScore || 0) : (entry._bd[sortKey] || 0);

        return (
          <div key={entry.id} style={{
            // Rank color is ALWAYS primary — own entries only get a gold left border + YOU badge
            border: "1px solid " + rank.border,
            borderRadius: 12, marginBottom: 8, overflow: "hidden",
            background: rank.bg,
            borderLeft: isOwn
              ? "4px solid #00C853"
              : totalRank < 3 ? "4px solid " + rank.border : "4px solid transparent",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", cursor: "pointer" }}
              onClick={() => setExpandedEntries(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, textAlign: "center" }}>
                  {rank.medal
                    ? <span style={{ fontSize: 22 }}>{rank.medal}</span>
                    : <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: "rgba(255,255,255,0.3)" }}>{index + 1}</span>
                  }
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: index < 3 ? 17 : 15, color: index === 0 ? "#FFD700" : totalRank === 1 ? "#E8E8F0" : totalRank === 2 ? "#CD7F32" : "rgba(255,255,255,0.75)" }}>
                      {entry.entryName}
                    </span>
                    {isOwn && <span style={{ fontSize: 14 }}>⚽</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 1 }}>{entry.userName}</div>
                  {canSeePicks && groupProgress.total > 0 && !knockoutInfo.knockoutStarted && (() => {
                    const gp = entryGamesPlayed(entry.picks);
                    return (
                      <div style={{ fontSize: 10, color: "rgba(255,215,0,0.55)", marginTop: 3, fontWeight: 600, letterSpacing: 0.3 }}>
                        {gp}/30 group games played
                      </div>
                    );
                  })()}
                  {canSeePicks && knockoutInfo.knockoutStarted && (() => {
                    const alive = entryRemainingTeams(entry.picks);
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginTop: 5 }}>
                        {alive.length > 0 ? (
                          <>
                            <span style={{ fontSize: 10, color: "rgba(255,215,0,0.55)", fontWeight: 700, marginRight: 2 }}>{alive.length} alive:</span>
                            {alive.map((t) => <Flag key={t.id} cc={t.cc} size={16} />)}
                          </>
                        ) : (
                          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", fontWeight: 600 }}>No teams remaining</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ textAlign: "right" }}>
                  <div>
                    <span style={{
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: index === 0 ? 32 : index < 3 ? 26 : 22,
                      color: totalRank === 0 ? "#FFD700" : totalRank === 1 ? "#D8DCE8" : totalRank === 2 ? "#CD7F32" : "rgba(255,255,255,0.65)",
                      textShadow: totalRank === 0 ? "0 0 16px rgba(255,215,0,0.5)" : totalRank === 1 ? "0 0 12px rgba(210,220,240,0.4)" : totalRank === 2 ? "0 0 10px rgba(205,127,50,0.4)" : "none",
                      lineHeight: 1
                    }}>
                      {sortScore}
                    </span>
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginLeft: 3 }}>PTS</span>
                  </div>
                  {sortKey !== "total" && (
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 1 }}>
                      Total: {entry.totalScore || 0}
                    </div>
                  )}
                </div>
                {isOwn && !contest.picksLocked && (
                  <button onClick={(e) => { e.stopPropagation(); onEditEntry(entry); }} className="btn btn-ghost btn-sm">Edit</button>
                )}
                <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 11 }}>{isExpanded ? "▲" : "▼"}</span>
              </div>
            </div>

            {isExpanded && (
              <div style={{ padding: "4px 12px 14px 12px", borderTop: "1px solid rgba(255,255,255,0.06)", background: "#101e30" }}>
                {canSeePicks ? (
                  <EntryBreakdown picks={entry.picks || {}} teamStatsMap={teamStatsMap} teamGamesPlayed={teamGamesPlayed} />
                ) : (
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.25)", fontStyle: "italic", padding: "12px 0" }}>
                    🔒 Picks and breakdown revealed when the tournament begins on June 11.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default Leaderboard;
